import { Editor, Extension } from '@tiptap/core';
import { MarkdownStorage } from 'tiptap-markdown';
import { SuggestionCandidate } from './mentions/typings';
import { getMentionRegexp } from './mentions/MarkdownMention';

interface EditorTypeOptions {
  type: 'text' | 'markdown' | 'html';
  isCourseEditor?: boolean;
}

type ContentParser = (content: string | undefined, isFocused: boolean) => void;
type ContentSerializer = () => string;

export function getContentParser(editor: Editor): ContentParser {
  const parseContent: ContentParser | undefined =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    editor.storage?.content?.parseContent;
  if (!parseContent) {
    throw new Error('Content parser is not available. Make sure the Content extension is enabled.');
  }

  return parseContent;
}

export function getContentSerializer(editor: Editor): ContentSerializer {
  const getContent: ContentSerializer | undefined =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    editor.storage?.content?.getContent;
  if (!getContent) {
    throw new Error(
      'Content serializer is not available. Make sure the Content extension is enabled.',
    );
  }
  return getContent;
}

function parseMentions(
  line: string,
  char: string = '@',
  items: SuggestionCandidate[] = [],
  parseKnownOnly = true,
) {
  const mentionPattern = getMentionRegexp(char);
  const matches = [...line.matchAll(mentionPattern)];
  let lastEnd = 0;
  const result = [];

  for (const match of matches) {
    const start = match.index! + (match[0].startsWith(' ') ? 1 : 0);
    const end = match.index! + match[0].length;

    // NOTE: Push the text before the mention
    if (start > lastEnd) {
      result.push({ type: 'text', text: line.substring(lastEnd, start) });
    }

    // NOTE: If we only parse mentions that are known from the items array
    if (parseKnownOnly) {
      // NOTE: Find the known mention by matching the start of the matched string(without the initiator char).
      // Eg. @Response 1asd will be @Response 1 and asd
      const suggestion = items.find(
        item => match[2].startsWith(item.id) || match[2].startsWith(item.label),
      );
      if (suggestion) {
        // NOTE: If we found the suggestion, push it as a mention
        result.push({
          type: 'mention',
          attrs: {
            id: suggestion.id,
            label: suggestion.label,
          },
        });
        // NOTE: Check if we have any remaining chars after the known mention and push it as a text
        const remainingChars = match[0].trim().replace(char, '').replace(suggestion.label, '');
        if (remainingChars.length) {
          result.push({
            type: 'text',
            text: remainingChars,
          });
        }
      } else {
        // NOTE: If we only want known mentions push the found mention as normal text
        result.push({ type: 'text', text: match[1] });
      }

      // NOTE: If we have an extra space after the mention keep it
      if (match[0].endsWith(' ')) {
        result.push({
          type: 'text',
          text: ' ',
        });
      }
    } else {
      // NOTE: If we want not known mentions as well, push the mention as a mention
      result.push({
        type: 'mention',
        attrs: {
          id: match[2].trim(),
          label: match[2].trim(),
        },
      });
    }

    lastEnd = end;
  }

  if (lastEnd < line.length) {
    result.push({ type: 'text', text: line.substring(lastEnd) });
  }

  return result;
}

export function generateParagraphContent(content: string, suggestion: any) {
  const jsonContent = content
    // split the text into lines
    .split('\n')
    // map each line to a node or a hard break node
    .map((line, index, arr) => {
      const isLast = index === arr.length - 1;

      if (line === '') {
        return isLast ? [] : [{ type: 'hardBreak' }];
      }

      let text: any[];
      if (suggestion) {
        text = parseMentions(line, suggestion.char, suggestion.items, suggestion.parseKnownOnly);
      } else {
        text = [
          {
            type: 'text',
            text: line,
          },
        ];
      }

      return isLast ? [...text] : [...text, { type: 'hardBreak' }];
    })
    .flat();
  return jsonContent;
}

export function generateJson(content: string, suggestion: any) {
  const jsonContent = generateParagraphContent(content, suggestion);

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: jsonContent.flat(),
      },
    ],
  };
}

const Content = Extension.create<EditorTypeOptions>({
  name: 'content',
  addOptions() {
    return {
      type: 'text',
    };
  },
  addStorage() {
    return {
      type: this.options.type,
    };
  },
  onBeforeCreate() {
    this.editor.storage.content = {
      type: this.options.type,
      getContent: () => {
        const type = this.options.type;
        const storage: MarkdownStorage | undefined = this.editor.storage.markdown;

        if (!storage && type === 'markdown') throw new Error('Markdown storage is not available');
        const content =
          type === 'text'
            ? this.editor.getText()
            : type === 'markdown'
            ? storage!.getMarkdown()
            : this.editor.getHTML();
        return content;
      },
      parseContent: (content: string | undefined, isFocused: boolean) => {
        const type = this.options.type;
        const editor = this.editor;
        const isFocus = isFocused || editor.isFocused;
        switch (type) {
          case 'text': {
            // only update the content if the editor is not focused
            if (!isFocus && content !== undefined && content !== editor.getText()) {
              const suggestion = editor.storage.mention;
              const json = generateJson(content, suggestion?.mention);
              setTimeout(() => {
                editor.commands.setContent(json, false);
              });
            }
            break;
          }
          case 'markdown': {
            const storage: MarkdownStorage | undefined = editor.storage.markdown;

            if (!storage && type === 'markdown')
              throw new Error('Markdown storage is not available');

            const currentContent = storage!.getMarkdown();

            // only update the content if the editor is not focused
            if (!isFocus && content !== undefined && content !== currentContent) {
              editor.commands.setContent(content, false, {
                preserveWhitespace: true,
              });
            }
            break;
          }
          case 'html': {
            const currentContent = editor.getHTML();
            if (!isFocus && content !== undefined && content !== currentContent) {
              setTimeout(() => {
                editor.commands.setContent(content, false, {
                  preserveWhitespace: 'full',
                });
              });
            }
          }
        }
      },
    };
  },
});

export default Content;
